import { state } from '../lib/state.js';
import { loadLineCalculatorSettings, saveLineCalculatorSettings } from '../services/repository.js';
import { startCountryBrowserlessJob } from '../services/browserless-country-dispatch.js';
import { els } from '../app/dom.js';
import { requireAuthenticatedSession } from '../app/auth.js';
import { setStatus, esc, sectionDistanceKm, formatDuration } from '../lib/app-helpers.js';
import { prepareSectionMetrics } from './common.js';

const DARSGO_SETTINGS_KEY = 'd24-darsgo-si-settings';
let pendingDarsgoSection = null;
let darsgoUi = null;

function ensureDarsgoDialog(){
  if(darsgoUi)return darsgoUi;
  const style=document.createElement('style');
  style.id='d24-darsgo-dialog-style';
  style.textContent=`
    #d24DarsgoDialog{position:fixed;inset:0;z-index:10000;display:flex;align-items:center;justify-content:center;padding:24px;background:rgba(15,23,42,.58)}
    #d24DarsgoDialog.hidden{display:none}
    #d24DarsgoDialog .d24-si-dialog{position:relative;width:min(700px,100%);max-height:90vh;overflow:auto;padding:24px;background:#fff;border-radius:16px;box-shadow:0 24px 70px rgba(0,0,0,.3);color:#172033}
    #d24DarsgoDialog h2{margin:0;color:#007749;font-size:24px}
    #d24DarsgoDialog .d24-si-route{margin:7px 0 18px;padding:10px 12px;background:#eef8f3;border-radius:9px;color:#405047;line-height:1.45}
    #d24DarsgoDialog .d24-si-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:14px}
    #d24DarsgoDialog label{display:flex;flex-direction:column;gap:6px;font-weight:700;color:#344054}
    #d24DarsgoDialog select{min-height:42px;padding:9px 10px;border:1px solid #cbd5e1;border-radius:8px;background:#fff;font:inherit;color:#172033}
    #d24DarsgoDialog .d24-si-note{margin-top:14px;padding:10px 12px;border:1px solid #d9e7df;border-radius:9px;background:#f7fbf9;color:#4b5d53;font-size:12px}
    #d24DarsgoDialog .d24-si-actions{display:flex;justify-content:flex-end;gap:10px;margin-top:20px}
    #d24DarsgoDialog button{border:0;border-radius:8px;padding:10px 16px;font-weight:700;cursor:pointer}
    #d24DarsgoDialog .d24-si-cancel{background:#e8edf3;color:#26364a}
    #d24DarsgoDialog .d24-si-submit{background:#007749;color:#fff}
    #d24DarsgoDialog .d24-si-close{position:absolute;right:14px;top:12px;background:transparent;color:#667085;font-size:24px;padding:4px 8px}
    @media(max-width:650px){#d24DarsgoDialog .d24-si-grid{grid-template-columns:1fr}}
  `;
  document.head.appendChild(style);

  const dialog=document.createElement('div');
  dialog.id='d24DarsgoDialog';
  dialog.className='hidden';
  dialog.setAttribute('aria-hidden','true');
  dialog.innerHTML=`
    <section class="d24-si-dialog" role="dialog" aria-modal="true" aria-labelledby="d24DarsgoTitle">
      <button type="button" class="d24-si-close" aria-label="Zamknij">×</button>
      <h2 id="d24DarsgoTitle">DarsGo Słowenia</h2>
      <div class="d24-si-route" id="d24DarsgoRoute"></div>
      <form id="d24DarsgoForm">
        <div class="d24-si-grid">
          <label>Kraj, z którego pojazd wjeżdża do Słowenii
            <select id="d24DarsgoEntryCountry" required>
              <option value="SLO">Słowenia — początek wewnątrz kraju</option>
              <option value="A">Austria</option>
              <option value="HR">Chorwacja</option>
              <option value="H">Węgry</option>
              <option value="I">Włochy</option>
            </select>
          </label>
          <label>Kraj, do którego pojazd wyjeżdża ze Słowenii
            <select id="d24DarsgoExitCountry" required>
              <option value="SLO">Słowenia — koniec wewnątrz kraju</option>
              <option value="A">Austria</option>
              <option value="HR">Chorwacja</option>
              <option value="H">Węgry</option>
              <option value="I">Włochy</option>
            </select>
          </label>
          <label>Liczba osi pojazdu łącznie z przyczepą
            <select id="d24DarsgoAxles" required>
              <option value="2">2 osie</option>
              <option value="3">3 osie</option>
              <option value="4+">4 lub więcej osi</option>
            </select>
          </label>
          <label>Klasa emisji EURO
            <select id="d24DarsgoEmission" required>
              <option value="E0">EURO 0</option><option value="E1">EURO I</option><option value="E2">EURO II</option>
              <option value="E3">EURO III</option><option value="E4">EURO IV</option><option value="E5">EURO V</option>
              <option value="E6">EURO VI</option><option value="EEV">EEV</option>
            </select>
          </label>
          <label>Klasa emisji CO₂
            <select id="d24DarsgoCo2" required>
              <option value="1">CO₂ 1</option><option value="2">CO₂ 2</option><option value="3">CO₂ 3</option>
              <option value="4">CO₂ 4</option><option value="5">CO₂ 5</option>
            </select>
          </label>
        </div>
        <div class="d24-si-note">DarsGo nie przyjmuje dowolnych przystanków. Automatyzacja Browserless wybiera kraj wjazdu i wyjazdu, a następnie dopasowuje właściwe punkty wjazdu/wyjazdu do słoweńskiej sieci autostrad na podstawie nazwy granicy i GPS.</div>
        <div class="d24-si-actions">
          <button type="button" class="d24-si-cancel">Anuluj</button>
          <button type="submit" class="d24-si-submit">Uruchom DarsGo</button>
        </div>
      </form>
    </section>`;
  document.body.appendChild(dialog);

  darsgoUi={
    dialog,
    route:dialog.querySelector('#d24DarsgoRoute'),
    form:dialog.querySelector('#d24DarsgoForm'),
    entryCountry:dialog.querySelector('#d24DarsgoEntryCountry'),
    exitCountry:dialog.querySelector('#d24DarsgoExitCountry'),
    axles:dialog.querySelector('#d24DarsgoAxles'),
    emission:dialog.querySelector('#d24DarsgoEmission'),
    co2:dialog.querySelector('#d24DarsgoCo2'),
  };
  dialog.querySelector('.d24-si-close').addEventListener('click',closeDarsgoDialog);
  dialog.querySelector('.d24-si-cancel').addEventListener('click',closeDarsgoDialog);
  dialog.addEventListener('click',event=>{if(event.target===dialog)closeDarsgoDialog();});
  darsgoUi.form.addEventListener('submit',submitDarsgoDialog);
  return darsgoUi;
}

export async function openDarsgoDialog(section){
  pendingDarsgoSection=section;
  const ui=ensureDarsgoDialog();
  const localDefaults=loadDarsgoSettings();
  let savedForLine=null;
  try{savedForLine=await loadLineCalculatorSettings(state.organizationId,state.currentRoute.lineNumber,'darsgo-si');}catch(error){console.warn('[D24 DARSGO] Parametry linii:',error);}
  const defaults={...localDefaults,...(savedForLine||{})};
  const entryPoint=section.points?.[0]||{};
  const exitPoint=section.points?.at(-1)||{};
  const entryAuto=inferDarsgoCountry(entryPoint);
  const exitAuto=inferDarsgoCountry(exitPoint);
  ui.entryCountry.value=defaults.entryCountry||entryAuto;
  ui.exitCountry.value=defaults.exitCountry||exitAuto;
  ui.axles.value=defaults.axles||'3';
  ui.emission.value=defaults.emissionClass||'E6';
  ui.co2.value=defaults.co2Class||'1';
  ui.route.innerHTML=`<strong>${esc(section.countryName)}:</strong> ${section.points.map(point=>esc(point.name)).join(' → ')}<br><small>Automatycznie: wjazd ${esc(entryAuto)}, wyjazd ${esc(exitAuto)}. W razie potrzeby możesz zmienić kraj ręcznie.</small>`;
  ui.dialog.classList.remove('hidden');
  ui.dialog.setAttribute('aria-hidden','false');
  document.body.style.overflow='hidden';
  setTimeout(()=>ui.entryCountry.focus(),0);
}

export function closeDarsgoDialog(){
  pendingDarsgoSection=null;
  if(!darsgoUi)return;
  darsgoUi.dialog.classList.add('hidden');
  darsgoUi.dialog.setAttribute('aria-hidden','true');
  document.body.style.overflow='';
}

export async function submitDarsgoDialog(event){
  event.preventDefault();
  if(!pendingDarsgoSection)return;
  const ui=ensureDarsgoDialog();
  if(!ui.form.reportValidity())return;
  const section=pendingDarsgoSection;
  const settings={
    system:'darsgo-si',
    entryCountry:ui.entryCountry.value,
    exitCountry:ui.exitCountry.value,
    axles:ui.axles.value,
    emissionClass:ui.emission.value,
    co2Class:ui.co2.value,
  };
  localStorage.setItem(DARSGO_SETTINGS_KEY,JSON.stringify(settings));
  try{await saveLineCalculatorSettings(state.organizationId,state.currentRoute.lineNumber,'darsgo-si',settings);}catch(error){return setStatus(els.extensionStatus,`Nie udało się zapisać parametrów DarsGo: ${error.message}`,'error');}
  closeDarsgoDialog();
  await runDarsgoSi(section,settings);
}

export async function runDarsgoSi(section,settings){
  const preparedSection=await prepareSectionMetrics(section,'słoweńskiego');
  if(!preparedSection)return;
  if(!await requireAuthenticatedSession('uruchomić kalkulator DarsGo Słowenia'))return;

  const distanceKm=sectionDistanceKm(preparedSection);
  const durationText=Number.isFinite(Number(preparedSection.durationSeconds))?formatDuration(Number(preparedSection.durationSeconds)):'';
  const entryPoint=preparedSection.points[0];
  const exitPoint=preparedSection.points.at(-1);
  const jobId=crypto.randomUUID();
  const payload={
    jobId,
    calculatorKey:'darsgo-si',
    routeId:state.currentRoute.id,
    routeVersionId:state.currentRoute.versionId,
    routeTitle:state.currentRoute.title,
    sourceFile:state.currentRoute.sourceFileName,
    countryCode:'SI',
    countryName:'Słowenia',
    sectionId:preparedSection.id,
    sectionOrder:preparedSection.order,
    distanceKm,
    durationText,
    entryCountry:settings.entryCountry,
    exitCountry:settings.exitCountry,
    entryPoint:copyDarsgoPoint(entryPoint),
    exitPoint:copyDarsgoPoint(exitPoint),
    points:preparedSection.points.map(copyDarsgoPoint),
    settings,
  };
  setStatus(els.extensionStatus,`Uruchamiam DarsGo w Browserless: ${settings.entryCountry} → ${settings.exitCountry}…`,'working');
  try{await startCountryBrowserlessJob(payload);}catch(error){console.error('[D24 BROWSERLESS DARSGO]',error);}
}

function copyDarsgoPoint(point){
  return {
    name:point?.name||'',
    address:point?.address||'',
    countryCode:point?.countryCode||'',
    countryName:point?.countryName||'',
    lat:point?.lat,
    lon:point?.lon,
    isBorder:Boolean(point?.isBorder),
    borderName:point?.borderName||'',
    resolvedDisplayName:point?.resolvedDisplayName||'',
  };
}

export function inferDarsgoCountry(point){
  if(!point?.isBorder)return 'SLO';
  const text=`${point.countryCode||''} ${point.countryName||''} ${point.name||''} ${point.borderName||''}`.toUpperCase();
  if(/\bAT\b|AUSTRIA/.test(text))return 'A';
  if(/\bHR\b|CHORWAC|CROAT/.test(text))return 'HR';
  if(/\bHU\b|WĘGR|WEGR|HUNGAR/.test(text))return 'H';
  if(/\bIT\b|WŁOCH|WLOCH|ITAL/.test(text))return 'I';
  return 'SLO';
}


export function loadDarsgoSettings(){
  try{return JSON.parse(localStorage.getItem(DARSGO_SETTINGS_KEY)||'{}');}
  catch{return {};}
}

